// @ts-nocheck
import React, { useEffect, useState } from 'react';
import { graphql } from 'gatsby';
import styled from 'styled-components';
import CollectionHeroSection from '../components/collection/CollectionHeroSection';
import CollectionMediaSection from '../components/collection/CollectionMediaSection';
import CollectionGridSection from '../components/collection/CollectionGridSection';
import ShopTheLookMediaSection from '../components/collection/ShopTheLookMediaSection';
import SEO from '../components/shared/SEO';
import { useGlobalComponentsQuery } from '../hooks/index';
import { desktopBreakpoint, colors, desktopVW, mobileVW, zIndex } from '../styles/index';
import LinkTool from '../components/shared/LinkTool';
import { useLocale } from '../hooks/layout/useLocale';
import ShelfMateApp from '../components/shelfmateApp';
import { useLocation } from '@reach/router';

export interface collectionProps {
  data: {
    allContentfulShelfmateApp: any;
    contentfulPageCollection: {
      title: string;
      seoTitle: string;
      seoDescription: string;
      heroImage: {
        fluid: string;
      };
      heroTopText: string;
      heroBottomText: string;
      mediaSection: {
        title: string;
        subtitle: string;
        paragraph: string;
        video: any;
        videoVimeo: string;
        videoTitle: string;
      };
      collectionAutometicByTagNew: boolean;
      items: {
        title: string;
        slug: string;
        shopifyData: {
          variants: {
            price: string;
            compare_at_price: string;
            inventory_quantity: string;
          };
        };
        productTag: string;
        thumbnailImages: {
          fluid: string;
        };
      };
      shopTheLook: {
        title: string;
        subtitle: string;
        paragraph: string;
        video: any;
        videoVimeo: string;
        videoTitle: string;
        image: {
          fluid: string;
        };
      };
    };
  };
}

const Collection = ({
  data: { contentfulPageCollection, allContentfulShelfmateApp },
}: collectionProps) => {
  const {
    seoTitle,
    seoDescription,
    heroImage,
    heroTopText,
    heroBottomText,
    mediaSection,
    collectionAutometicByTagNew,
    items,
    shopTheLook,
  } = contentfulPageCollection;
  const location = useLocation();
  const global = useGlobalComponentsQuery();
  const [isCollection, setCollection] = useState('second-chance');

  useEffect(() => {
    if (location.pathname.includes('second-chance') || location.pathname.includes('tweedekans')) {
      setCollection('second-chance');
    } else if (location.pathname.includes('shelfmate-app')) {
      setCollection('shelfmate-app');
    } else {
      setCollection('collection');
    }
  }, [location]);

  if (isCollection === 'shelfmate-app')
    return <ShelfMateApp product={items} dataApp={allContentfulShelfmateApp.edges[0].node} />;

  return (
    <>
      <SEO title={seoTitle} description={seoDescription} image={global?.seoImage.file.url} />
      <CollectionHeroSection
        heroImage={heroImage}
        heroTopText={heroTopText}
        heroBottomText={heroBottomText}
      />
      {mediaSection && <CollectionMediaSection mediaSection={mediaSection} />}
      {items !== null && <CollectionGridSection items={items} />}
      {shopTheLook?.length > 0 ? (
        <ItemShowTheLookWrapper>
          {shopTheLook?.map((item: any, i: number) => {
            return (
              <ItemShowTheLookContent>
                <ItemShowTheLook to={`/${item?.collection?.slug}/`} style={{ width: '101%' }}>
                  <ShopTheLookMediaSection mediaSection={item} key={i} />
                </ItemShowTheLook>
              </ItemShowTheLookContent>
            );
          })}
        </ItemShowTheLookWrapper>
      ) : null}
    </>
  );
};

const ItemShowTheLookContent = styled.div`
  width: 100%;
  ${desktopBreakpoint} {
    width: auto;
  }
`;

const ItemShowTheLookWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  padding: ${mobileVW(50)} ${mobileVW(15)} ${mobileVW(0)} ${mobileVW(15)};

  ${desktopBreakpoint} {
    padding: ${desktopVW(78)} ${desktopVW(45)} ${desktopVW(58)} ${desktopVW(45)};
  }
`;

const ItemShowTheLook = styled(LinkTool)`
  width: 100%;
  ${desktopBreakpoint} {
    width: auto;
  }
`;

export default Collection;

export const CollectionPageQuery = graphql`
  query CollectionPage($id: String!) {
    allContentfulShelfmateApp(limit: 1) {
      edges {
        node {
          playgroundTitle {
            playgroundTitle
          }
          urlPinterest
          textButtonPinterestDesktop
          textButtonPinterestMobile
          titlePopupPinterest
          descriptionPopupPinterest
          textTitleModalAddTemplate
          textDescriptionModalAddTemplate
          textButtonAgreeModalAddTemplate
          textButtonDisagreeModalAddTemplate
          textTitleModalReset
          textDescriptionModalReset
          textButtonAgreeModalReset
          textButtonDisagreeModalReset
          textButtonForNavigationToOrderDetail
          textSidebarBeforePrice
          textSidebarAfterPrice
          textSidebarBeforeProduct
          textSidebarScreenViewOrderDetail
          textButtonReset
          textTitleTutorial1
          textDescriptionTutorial1
          textTitleTutorial2
          textDescriptionTutorial2
          showButtonShelfmateApp
          shelfmateDiscount
          shelfmatePopUpDiscount {
            title
            description
            fluid {
              aspectRatio
              src
              srcSet
              srcWebp
              srcSetWebp
              sizes
            }
          }
          iconCategoryShelfmateApp {
            title
            fluid {
              aspectRatio
              src
              srcSet
              srcWebp
              srcSetWebp
              sizes
            }
          }
        }
      }
    }
    contentfulPageCollection(id: { eq: $id }) {
      title
      seoTitle
      seoDescription
      heroImage {
        fluid(maxWidth: 1920, quality: 50) {
          ...GatsbyContentfulFluid_withWebp_noBase64
        }
      }
      heroTopText
      heroBottomText
      mediaSection {
        title
        heading
        subtitle {
          subtitle
        }
        paragraph {
          paragraph
        }
        invertedLayout
        borderlessImage
        video {
          title
          file {
            url
          }
        }
        videoVimeo
        videoTitle
      }
      collectionAutometicByTagNew
      items {
        title
        titleToShow
        slug
        shopifyData {
          variants {
            title
            price
            compare_at_price
            inventory_quantity
            sku
            id
          }
        }
        thumbnailVariantsImages: variantsImages {
          variantTitle
          productName
          images {
            title
            fluid(maxWidth: 700, quality: 50) {
              ...GatsbyContentfulFluid_withWebp_noBase64
            }
          }
          thumbnailForHover {
            fluid(maxWidth: 1920, quality: 100) {
              ...GatsbyContentfulFluid_withWebp_noBase64
            }
          }
          thumbnailVariantColor {
            fluid(maxWidth: 1920, quality: 100) {
              ...GatsbyContentfulFluid_withWebp_noBase64
            }
          }
        }
        productTag
        shelfmateGroup
        shelfmateGroupType
        shelfmateImageTransparent {
          title
          fluid(maxWidth: 1920, quality: 100) {
            ...GatsbyContentfulFluid_withWebp_noBase64
          }
        }
        thumbnailImages {
          fluid {
            ...GatsbyContentfulFluid_withWebp_noBase64
          }
        }
      }
      shopTheLook {
        title
        heading
        subtitle {
          subtitle
        }
        paragraph {
          paragraph
        }
        invertedLayout
        borderlessImage
        video {
          title
          file {
            url
          }
        }
        videoVimeo
        videoTitle
        image {
          fluid(maxWidth: 1920, quality: 100) {
            ...GatsbyContentfulFluid_withWebp_noBase64
          }
        }
        collection {
          slug
        }
      }
    }
  }
`;
